import React from 'react'
import { FormControl, Flex, ButtonIcon } from 'front-end/src/components'
import DateTimePickers from '../../../../../components/common/date-time-pickers/DateTimePickers'
import dayjs, { Dayjs } from 'shared/dayjs'
import IconClock from 'front-end/src/components/common/icon/IconClock'
import IconGlobe from 'front-end/src/components/common/icon/IconGlobe'
import { FormLabelIcon } from '../../../components/event-form/EventFormLabelIcon'
import { useModal } from 'front-end/src/app/hooks/useModal'
import { EventTimezoneModal } from './EventTimezoneModal'
import { useFormikContext } from 'formik'
import { EventDetailEditFragment } from '../../EventEdit.codegen'
import { useManageEventContext } from 'front-end/src/pages/manage-events/ManageEventContext'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import { brandColors } from 'front-end/../shared/basicPalette'
import { formatTimezone } from 'shared/components/event-formatted-time/eventDateRangeFormatter'
import { getDefaultEventTime } from 'front-end/src/pages/manage-events/event-edit/tabs/event-detail/eventDetailUtils'

const EventDetailTime = () => {
  const { event, isRecurringInstance, isRecurringSeries } =
    useManageEventContext()
  const { setFieldValue, values, setFieldTouched } =
    useFormikContext<EventDetailEditFragment>()
  const { open, currentModal: timezoneModal } = useModal()
  const { defaultStartTime, defaultEndTime } = getDefaultEventTime()

  // Default event time is set when creating events since rec. events
  // but for older events let's keep this default values in case start+end times are empty
  if (!values.start_time) {
    setFieldValue('start_time', defaultStartTime)
  }

  if (!values.end_time) {
    setFieldValue('end_time', defaultEndTime)
  }

  const isRegularEvent =
    !!values.recurringEvent?.recurrence?.freq &&
    !isRecurringInstance &&
    !isRecurringSeries

  const { timezone, start_time, end_time } = values

  return (
    <>
      <Flex mb="16px" flexDirection="column">
        <Flex direction={['column', 'column', 'row']}>
          <FormControl
            as={Flex}
            flex={1}
            w="auto"
            id="datetime_from"
            mb={['8px', '8px', 0]}
          >
            <FormLabelIcon icon={<IconClock aria-label="Datetime setup" />} />
            <Flex>
              <DateTimePickers
                disablePastDays={isRegularEvent}
                timezone={timezone}
                isUtc
                value={
                  dayjs.isDayjs(start_time) ? start_time : dayjs(start_time)
                }
                onChange={(newDate: Dayjs | null) => {
                  if (!newDate || !newDate.isValid()) return

                  setFieldValue('start_time', newDate)
                  setFieldTouched('start_time', newDate !== event.start_time)

                  setFieldValue(
                    'end_time',
                    dayjs.utc(newDate || undefined).add(1, 'hour')
                  )
                }}
                dataTest="FromDatePicker"
              />
            </Flex>
          </FormControl>
          <Flex flexGrow={1}>
            <FormControl as={Flex} w="auto" id="datetime_to">
              <Flex py="8px" pr="16px" pl={['6px', '6px', '16px']}>
                to
              </Flex>
              <Flex>
                <DateTimePickers
                  disablePastDays={isRegularEvent}
                  timezone={timezone}
                  isUtc
                  value={dayjs.isDayjs(end_time) ? end_time : dayjs(end_time)}
                  onChange={(newDate: Dayjs | null) => {
                    if (!newDate || !newDate.isValid()) return

                    setFieldValue('end_time', newDate)
                    setFieldTouched('end_time', newDate !== event.end_time)

                    if (newDate.isBefore(start_time || undefined)) {
                      setFieldValue(
                        'start_time',
                        dayjs.utc(newDate || undefined).subtract(1, 'hour')
                        // for some reason dayjs does not keep the TZ when cloning
                      )
                    }
                  }}
                  dataTest="ToDatePicker"
                />
              </Flex>
            </FormControl>
            <FormControl
              alignItems="center"
              as={Flex}
              w="auto"
              ml="16px"
              flexShrink={1}
              id="timezone"
            >
              <ButtonIcon
                variant="outline"
                aria-label="Set timezone"
                icon={<IconGlobe />}
                onClick={() =>
                  open(EventTimezoneModal, { timezone }).then((value) =>
                    setFieldValue('timezone', value || null)
                  )
                }
                dataTest="TimezoneButton"
              />
            </FormControl>
          </Flex>
        </Flex>
        {timezone && (
          <Flex
            ml="38px"
            mt="4px"
            fontSize={fontSizes.ssm}
            color={brandColors.grey[500]}
          >
            {formatTimezone(timezone)}
          </Flex>
        )}
      </Flex>

      {timezoneModal}
    </>
  )
}

export default React.memo(EventDetailTime)
